import { deriveShades, adjustLightness, hexToRgb, deriveSectionBackground } from '@kevlarcon/pages';
import { TenantBranding } from '../api/auth';

const rgbTriplet = (hex: string): string => hexToRgb(hex).join(', ');

/**
 * Generic, unbranded-tenant fallbacks — mirrors the defaults
 * OrganizationBrandingSerializer applies server-side when an org hasn't set a
 * field yet, so an org with no theme still renders something coherent.
 */
const FALLBACK = {
  primary_color: '#4F46E5',
  background_color: '#F9FAFB',
  text_color: '#111827',
  border_color: '#D1D5DB',
  accent_color: '#F59E0B',
};

export interface ThemePalette {
  burgundy: string; burgundyDeep: string; burgundySoft: string;
  gold: string; goldDeep: string; goldPale: string;
  paper: string; ivory: string; parchment: string; paperWarm: string;
  ink: string; inkSoft: string; inkMute: string;
  rule: string; ruleSoft: string;
  /** "R, G, B" triplets for the handful of tones ocen.css also uses inside a
   * translucent `rgba(...)` overlay (photo captions, footer hairlines, hero
   * glows) — a CSS `var()` alone can't add an alpha channel to a hex color. */
  ivoryRgb: string; goldRgb: string; paperRgb: string;
  burgundyDeepRgb: string; inkRgb: string;
  /** Backs every section's `◐` dark/light background toggle — see
   * `pages.models.Section.background` and `deriveSectionBackground`'s
   * docstring. */
  sectionBgDark: string; sectionTextDark: string;
  sectionBgLight: string; sectionTextLight: string;
  /** A muted tint of `sectionBgDark`, for the one map fill (the
   * "neighboring state" tier) that has no dedicated color of its own — see
   * `deriveMapPalette`'s dark case. Exposed here (not just computed inline
   * inside `deriveMapPalette`) so `ocen.css`'s dark-mode legend swatch can
   * match the canvas exactly via `--map-neighbor-dark`. */
  mapNeighboringDark: string;
}

/**
 * The single source of truth for turning an org's 5 stored theme fields into
 * every derived tone ocen.css's color families expect. Shared by
 * `applyOrgTheme` (sets the CSS custom properties the stylesheet reads) and
 * `useThemePalette` (feeds StateMap's MapLibre paint properties, which can't
 * read CSS custom properties at all) so the live site and the map never
 * disagree about what the theme currently is.
 */
export function deriveThemePalette(branding?: Partial<TenantBranding>): ThemePalette {
  const primary = branding?.primary_color || FALLBACK.primary_color;
  const background = branding?.background_color || FALLBACK.background_color;
  const text = branding?.text_color || FALLBACK.text_color;
  const border = branding?.border_color || FALLBACK.border_color;
  const accent = branding?.accent_color || FALLBACK.accent_color;

  const burgundyShades = deriveShades(primary);
  const goldShades = deriveShades(accent);
  const ivory = adjustLightness(background, 3);
  const sectionDark = deriveSectionBackground('dark', { primary_color: primary, accent_color: accent, background_color: background, text_color: text });
  const sectionLight = deriveSectionBackground('light', { primary_color: primary, accent_color: accent, background_color: background, text_color: text });

  return {
    burgundy: burgundyShades.base,
    burgundyDeep: burgundyShades.deep,
    burgundySoft: burgundyShades.soft,
    gold: goldShades.base,
    goldDeep: goldShades.deep,
    goldPale: goldShades.soft,
    paper: background,
    ivory,
    parchment: adjustLightness(background, -6),
    paperWarm: adjustLightness(background, -4),
    ink: text,
    inkSoft: adjustLightness(text, 17),
    inkMute: adjustLightness(text, 34),
    rule: border,
    ruleSoft: adjustLightness(border, 6),
    ivoryRgb: rgbTriplet(ivory),
    goldRgb: rgbTriplet(goldShades.base),
    paperRgb: rgbTriplet(background),
    burgundyDeepRgb: rgbTriplet(burgundyShades.deep),
    inkRgb: rgbTriplet(text),
    sectionBgDark: sectionDark.background,
    sectionTextDark: sectionDark.text,
    sectionBgLight: sectionLight.background,
    sectionTextLight: sectionLight.text,
    mapNeighboringDark: adjustLightness(sectionDark.background, 10),
  };
}
